<template>
  <div
    v-if="$store.state.News.errorState.length < 1"
    class="c-login-error"
  >{{$store.state.News.errorState}}</div>

  <div class="p-page-create-edit" v-else-if="!loading">
    <page-title
      marginBottom="0"
      subTitle="Drag and drop elements into the main page to create your bespoke article layout"
    >
      <span v-if="editMode">Edit News Article</span>
      <span v-else>Create News Article</span>
    </page-title>

    <message v-if="duplicatePage" type="error">
      <h3>There's a problem</h3>
      <p>
        An article with the slug
        <strong>{{duplicatePage.slug}}</strong> already exists.
      </p>
    </message>
    <section-block :headerOverflowHidden="true">
      <template slot="header">
        <form-field
          dynamic-width
          ghost
          large
          min-width="312px"
          v-model="currentArticle.title"
          v-validate="{required: true}"
          name="title"
          placeholder="Add your article title here..."
          :error="errors.first('title')"
        ></form-field>
      </template>

      <template slot="actions" v-if="editMode">
        <base-button
          @click="onClickViewArticle"
          v-if="currentArticle.status === 'approved'"
        >View Article</base-button>
        <base-button
          primary="true"
          @click="onClickUpdate"
          iconBefore="edit"
          v-if="userCanAdd"
          :disabled="disabled"
        >{{currentArticle.status == 'rejected' ? 'Re-submit':'Update'}}</base-button>
        <base-button
          danger
          @click="onClickReject"
          :disabled="disabled"
          v-if="currentArticle.status === 'pending' && userCanApprove"
        >Reject</base-button>
        <base-button
          success
          @click="onClickApprove"
          :disabled="disabled"
          v-if="currentArticle.status === 'pending' && userCanApprove"
        >Approve</base-button>
      </template>
      <template slot="actions" v-else>
        <base-button primary="true" @click="onClickSubmit" :disabled="disabled">Submit</base-button>
      </template>
      <page-builder
        :pageId="articleid"
        @reviewDateUpdated="(newDate) => articlereview_date = newDate"
        :activeSbPanel="activeSbPanel"
        :load-pages="false"
      >
        <template slot="preFields">
          <banner-upload
            name="default_image_id"
            :image="currentArticle.default_image"
            :docType="docType"
            :fileTypes="documentFileTypes"
            :resource="docType.singular"
            @uploaded="onFileUploaded"
            :bypassDuplicate="true"
            v-model="currentArticle.title"
            @handleBannerChange="handleBannerChange"
          >
            <p>Upload a default banner for your news article here</p>
          </banner-upload>
        </template>

        <template slot="intro">
          <div class="c-pb__no-elements--landscape">
            <div class="c-pb__no-elements-text">
              <p>Ready to add content to your article?</p>
              <p>
                Select
                <strong>&lsquo;Customise Article&rsquo;</strong> tab in the right hand menu
              </p>
            </div>
            <inline-svg name="page-builder-landscape"></inline-svg>
          </div>
        </template>
        <tabs slot="tabs">
          <tabs-item
            :active="activeSbPanel === 'settings'"
            @click="activeSbPanel = 'settings'"
          >Article Settings</tabs-item>

          <tabs-item
            :active="activeSbPanel === 'elements'"
            @click="activeSbPanel = 'elements'"
          >Customise Article</tabs-item>
        </tabs>

        <div slot="sidebars">
          <div class="c-pb__sb-settings" v-show="activeSbPanel === 'settings'">
            <form-field
              label="Categories"
              name="categories"
              helper-text="Select the categories you want the article to appear in"
              :required="true"
            >
              <form-multiselect
                :show-valid-tick="true"
                placeholder="Select category"
                v-model="currentArticle.categories"
                v-if="newsCategories"
                v-validate="{required: true}"
                :options="newsCategories"
                :multiple="true"
                label="title"
                track-by="id"
                @input="updateCategory"
                data-vv-as="category"
                id="category"
                name="category"
                :error="errors.first('category')"
              ></form-multiselect>
              <p v-else>Loading</p>
            </form-field>

            <form-field
              label="Interest Groups"
              name="interest_groups"
              helper-text="Help users find your article by tagging related interest groups:"
            >
              <form-multiselect
                :show-valid-tick="true"
                v-model="currentArticle.interestgroups"
                :options="interestGroups"
                :multiple="true"
                label="name"
                track-by="name"
                :taggable="true"
              ></form-multiselect>
            </form-field>

            <form-field
              label="Add Tags"
              name="document_tags"
              :helper-text="'Help users find your news article by tagging relevant keywords'"
            >
              <form-multiselect
                :show-valid-tick="true"
                v-model="currentArticle.tags"
                :options="tags"
                :multiple="true"
                label="name"
                track-by="name"
                :taggable="true"
                @tag="addTag"
              ></form-multiselect>
            </form-field>

            <form-field
              label="Publish Date"
              name="publish_date"
              :helper-text="'Choose the date you want the article to be published'"
              v-if="show_date"
            >
              <form-datepicker
                small
                v-model="currentArticle.publish_date"
                name="publish_date"
                format="DD / MM / YYYY"
                @input="onPublishDateInput"
                input-class="c-form__input"
              ></form-datepicker>
            </form-field>
          </div>
        </div>
      </page-builder>
    </section-block>
    <modal
      v-if="rejectModalOpen"
      class="ta-center"
      show-close-button
      @close="rejectModalOpen = false"
    >
      <h1 slot="header">Reason for Rejection</h1>
      <form method="post" action v-on:submit.prevent="rejectArticle">
        <form-field
          type="textarea"
          placeholder="Please specify a reason for rejection"
          v-model="rejectionReason"
          required
        />
        <base-button type="submit" :primary="true">Submit Reason</base-button>
      </form>
    </modal>
  </div>

  <loading v-else></loading>
</template>

<script>
/**
 * TODO: This is based on the page builder for content, but needs altering to
 * fit the needs of the news section.  Ideally the same 'could' be used for events
 */
import PageBuilder from "@/components/PageBuilder";
import FileUpload from "@/components/FileUpload";
import { BannerUpload, FormToggle } from "@/components/forms";
import filesApi from "@/api/files";
import { mapState, mapGetters } from "vuex";
import { ImageLoader, InlineLabel } from "@/components/elements";
import UserHasPermission from "@/mixins/UserHasPermission";

export default {
  name: "admin-page-edit-create",

  components: {
    PageBuilder,
    FileUpload,
    BannerUpload,
    FormToggle,
    ImageLoader,
    InlineLabel
  },

  mixins: [UserHasPermission],

  data() {
    return {
      editMode: false,
      loading: false,
      duplicatePage: null,
      activeSbPanel: "settings",
      articlestatus: "",
      articleid: 0,
      dataArticle: {},
      image_uploaded: false,
      docType: {
        plural: "news",
        single: "news"
      },
      documentFileTypes: ["gif", "png", "jpg", "jpeg"],
      bannerSrc: null,
      show_date: false,
      originalPageElements: {},
      featuredOriginal: false,
      disabled: true,
      rejectModalOpen: false,
      rejectionReason: ""
    };
  },

  beforeRouteLeave(to, from, next) {
    if (this.elementsHaveChanged && this.editMode) {
      this.$store.dispatch("createDialog", {
        messageText:
          "Any changes you made will be lost if you navigate away from this page, are you sure?",
        confirm: () => {
          next();
        }
      });
    } else {
      next();
    }
  },

  beforeCreate() {
    this.$store.dispatch("tags/list", { flat: true });
    // get a list of all the news categories
    this.$store
      .dispatch("NewsCategories/list", { flat: true })
      .catch(err => alert(err));
  },

  created() {
    this.loading = true;
    // reset
    this.$store.commit("pageElements/setPageElements", []);

    const { id } = this.$route.params || null;

    const { duplicate } = this.$route.query;

    this.$store.commit("News/RESET_ARTICLE");
    if (id) {
      this.editMode = true;
      this.retrievePage();
    } else {
      this.loading = false;
      this.disabled = false;
    }

    if (duplicate) {
      this.loading = true;
      this.retrievePage(duplicate, true);
    }
  },

  methods: {
    rejectArticle() {
      const vm = this;
      // do the following, but post the rejection reason along with the id i guess
      this.$store
        .dispatch("News/reject", {
          id: this.currentArticle.id,
          reason: this.rejectionReason
        })
        .then(resp => {
          this.rejectionReason = "";
          this.rejectModalOpen = false;
          vm.$store.dispatch(
            "notifications/create",
            {
              body: `The article was rejected`,
              type: "success"
            },
            { root: true }
          );
        });
    },
    handleFeatureToggle(val) {
      this.currentArticle.is_homepage_banner = val;
    },
    onBannerUploaded(file) {
      this.currentArticle.banner_id = file.id;
    },
    onPublishDateInput(value) {
      this.currentArticle.publish_date = this.$dayjs(value).format(
        "YYYY-MM-DD HH:mm:ss"
      );
    },
    updateSlug(val) {
      this.currentArticle.slug = val;
    },
    updateCategory(val) {
      // needs to be just ids (?)
      const catIds = val.map(item => item.id);
      // this.currentArticle.categories = catIds;
      this.currentArticle.categories = val;
    },
    updateDate(val) {
      this.currentArticle.publish_date = val;
    },
    retrievePage(pageId, setDuplicate) {
      this.loading = true;
      pageId = pageId || this.$route.params.id;
      // reset banner
      localStorage.removeItem("mft_store_getFixedBanner");
      this.$store.dispatch("getFixedBanner");
      this.$store.dispatch("News/read", pageId).then(resp => {
        /**
         * Get the response from the promise
         */
        this.loading = false;

        if (this.currentArticle.banner && this.currentArticle.banner.id) {
          this.currentArticle.banner_id = this.currentArticle.banner.id;
        }

        this.retrievePageElements(resp);
      });

      this.image_uploaded = true;
    },

    async retrievePageElements(page) {
      let elements;
      try {
        elements = await this.$store.dispatch("News/fetchElements", page.id);
        this.$store.commit("pageElements/setPageElements", elements);
        this.originalPageElements = elements ? elements.slice() : [];
        this.loading = false;
        this.disabled = false;
      } catch (err) {
        elements = [];
        this.$store.commit("pageElements/setPageElements", elements);
        this.originalPageElements = elements ? elements.slice() : [];
        this.loading = false;
        this.disabled = false;
      }
    },

    onClickApprove() {
      const vm = this;
      this.$store
        .dispatch("News/approve", this.currentArticle.id)
        .then(page => {
          this.retrievePage();
          vm.$store.dispatch(
            "notifications/create",
            {
              body: `The article was approved`,
              type: "success"
            },
            { root: true }
          );
        });
    },

    onClickReject() {
      /**
       * For this, we need to give a reason as to why we are rejecting
       */
      this.rejectModalOpen = true;
      return;
    },

    onClickViewArticle() {
      const route = this.$router.resolve(
        `/news/article/${this.currentArticle.id}`
      );
      window.open(route.href, "_blank");
    },

    //this is for saving a new article - when clicking the save draft button
    onSave() {
      // At thTODO: Same as below, but for saving as a draft.  Just update the status?
      // On submit, map the pageElements to the article
      const vm = this;
      this.$validator.validate().then(result => {
        if (!result) {
          vm.showValidationMessage = true;
        } else {
          vm.showValidationMessage = false;
          vm.currentArticle.elements = [
            ...this.$store.state.pageElements.pageElements
          ];

          vm.loading = true;

          //update categories
          const categories = this.currentArticle.categories.map(cat => cat.id);
          this.currentArticle.categories = categories;

          //interest groups
          if (
            this.currentArticle.interestgroups &&
            this.currentArticle.interestgroups.length > 0
          ) {
            this.currentArticle.interestgroup_ids = this.currentArticle.interestgroups.map(
              grp => grp.id
            );
            delete this.currentArticle.interestgroups;
          }

          //tags
          if (this.currentArticle.tags && this.currentArticle.tags.length > 0) {
            this.currentArticle.tag_ids = this.currentArticle.tags.map(
              grp => grp.id
            );
            delete this.currentArticle.tags;
          }

          this.$store
            .dispatch("News/create", this.currentArticle)
            .then(page => {
              vm.loading = false;

              this.$store.commit("pageElements/setPageElements", []);
              this.$router.push({
                name: "AdminNewsEdit",
                params: { id: page.data.data.article[0].id }
              });
            })
            .catch(error => {
              vm.loading = false;
              console.log("addedit error", { ...error });
              if (error.meta && error.meta.duplicate_page) {
                this.duplicatePage = error.meta.duplicate_page;
              }
            });
        }
      });
    },
    onClickSubmit() {
      // here we need to just set the status to pending
      if (this.editMode) {
        this.onClickUpdate();
      } else {
        this.onSave();
      }
    },

    onClickUpdate() {
      const { page, $store, currentArticle } = this;

      this.$validator.validate().then(result => {
        if (!result) {
          this.showValidationMessage = true;
        } else {
          const pageElments = $store.getters["pageElements/forServer"];

          const editedPage = JSON.parse(JSON.stringify(currentArticle));

          // add elements to the current page.
          editedPage.elements = pageElments;

          //update categories
          const categories = editedPage.categories.map(cat => cat.id);
          editedPage.categories = categories;

          //interest groups
          if (
            editedPage.interestgroups &&
            editedPage.interestgroups.length > 0
          ) {
            editedPage.interestgroup_ids = editedPage.interestgroups.map(
              grp => grp.id
            );

            delete editedPage.interestgroups;
          }

          //tags
          if (editedPage.tags && editedPage.tags.length > 0) {
            editedPage.tag_ids = editedPage.tags.map(grp => grp.id);
            delete editedPage.tags;
          }

          // we only need to post certain nodes, so remove any we don't need and put the rest in a payload object
          const {
            user,
            user_favourite,
            website_url,
            self,
            modified_user,
            modified_time,
            favourite_count,
            default_image,
            created_user,
            created_time,
            ...payload
          } = editedPage;

          this.loading = true;
          const vm = this;
          const featured = this.currentArticle.is_homepage_banner;

          //depending on the articles current status, we need to either update or resubmit
          const action = "News/resubmit";

          this.$store
            .dispatch(action, {
              pageId: this.currentArticle.id,
              pageData: payload
            })
            .then(response => {

              this.$store.commit("pageElements/setPageElements", []);
              this.$store.commit("News/SET_ARTICLE", []);
              this.retrievePage();
            })
            .catch(error => {
              this.loading = false;
              if (error.meta && error.meta.duplicate_page) {
                this.duplicatePage = error.meta.duplicate_page;
              }
            });
        }
      });
    },

    onFileUploaded(fileData) {
      this.$store.commit("News/SET_BANNER_ID", fileData.id);
    },

    handleBannerChange() {
      this.$store.commit("News/SET_BANNER_ID", null);
    },

    addTag(newTag) {
      const tagInfo = {
        id: null,
        name: newTag
      };

      this.currentArticle.tags.push(tagInfo);
      this.$store.dispatch("tags/add", newTag);
    }
  },

  computed: {
    ...mapState("News", ["currentArticle"]),
    ...mapState({
      interestGroups: state => state.InterestGroups.collection
    }),
    ...mapState({
      tags: state => state.tags.collection
    }),
    newsCategories() {
      return this.$store.getters["NewsCategories/formatCategories"];
    },
    userCanApprove() {
      return this.userHasPermission("publish_news");
    },
    userCanAdd() {
      return this.userHasPermission("add_edit_news");
    },
    // TODO: This has been copied from the pageElements
    elementsHaveChanged() {
      try {
        if (this.originalPageElements.length !== this.elementList.length) {
          return true;
        }

        for (let i = this.originalPageElements.length; i--; ) {
          if (this.originalPageElements[i] !== this.elementList[i]) {
            return true;
          }
        }
      } catch (err) {
        // console.log(err.message);
      }

      return false;
    },
    elementList() {
      return this.currentArticle.elements;
    }
  }
};
</script>


<style lang="scss">
.p-page-create-edit {
  .c-section-block__header-main .c-form__group {
    margin: 0;
  }
}

.default-image-holder {
  min-height: 300px;
  margin-top: 20px;
  background: $colour-nhs-pale-grey;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  width: 100%;

  .c-media-image {
    background: transparent;
  }

  .default_image__preview {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: 0 0;
    z-index: 0;
    &:after {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      content: "";
      background: rgba(0, 0, 0, 0.3);
    }
  }

  .default_image__button {
    position: relative;
    z-index: 1;
  }
}

.c-pb__no-elements--landscape {
  position: relative;

  .c-inline-svg,
  .c-pb__no-elements-text {
    position: absolute;
  }

  .c-pb__no-elements-text {
    text-align: left;
    left: 33%;
    z-index: 5;
  }
}
</style>
